/**
 * 帧率采样：用 requestAnimationFrame 的相邻时间差（performance.now）算帧耗时分布。
 *
 * 为什么不用 FPS 平均值：平均 58fps 完全可能包含若干 120ms 的卡顿帧，
 * 用户感知到的是卡顿，不是平均。所以报告里同时给出：
 *   p50 / p95 帧耗时、最差帧、掉帧数（> 2 帧 @60Hz ≈ 33.4ms）、长帧数（> 50ms）。
 *
 * 纯计算部分（computeFrameReport / percentile）不依赖 DOM，可被 vitest 直接测。
 */

export interface FrameReport {
  /** 参与统计的帧数 */
  readonly frames: number;
  /** 测量时长（ms） */
  readonly durationMs: number;
  /** 平均帧率 */
  readonly avgFps: number;
  /** 中位帧耗时（ms） */
  readonly p50FrameMs: number;
  /** 95 分位帧耗时（ms）—— 滚动是否「丝滑」主要看这个数 */
  readonly p95FrameMs: number;
  /** 最长帧耗时（ms） */
  readonly maxFrameMs: number;
  /** 由 p95 折算的最差帧率 */
  readonly worstFps: number;
  /** 掉帧数：帧耗时 > 33.4ms（60Hz 下丢 2 帧及以上） */
  readonly droppedFrames: number;
  /** 长帧数：帧耗时 > 50ms（会被 INP/长任务统计捕捉到） */
  readonly longFrames: number;
  /** 掉帧占比 */
  readonly droppedRatio: number;
}

export function percentile(sortedValues: readonly number[], p: number): number {
  if (sortedValues.length === 0) return 0;
  const clamped = Math.min(1, Math.max(0, p));
  const index = Math.min(sortedValues.length - 1, Math.max(0, Math.ceil(clamped * sortedValues.length) - 1));
  return sortedValues[index] ?? 0;
}

export function computeFrameReport(deltas: readonly number[]): FrameReport {
  const clean = deltas.filter((delta) => Number.isFinite(delta) && delta > 0);
  const frames = clean.length;
  if (frames === 0) {
    return {
      frames: 0,
      durationMs: 0,
      avgFps: 0,
      p50FrameMs: 0,
      p95FrameMs: 0,
      maxFrameMs: 0,
      worstFps: 0,
      droppedFrames: 0,
      longFrames: 0,
      droppedRatio: 0,
    };
  }

  const sorted = [...clean].sort((a, b) => a - b);
  const durationMs = clean.reduce((total, delta) => total + delta, 0);
  const p50 = percentile(sorted, 0.5);
  const p95 = percentile(sorted, 0.95);
  const max = sorted[sorted.length - 1] ?? 0;
  const dropped = clean.filter((delta) => delta > 33.4).length;
  const long = clean.filter((delta) => delta > 50).length;

  return {
    frames,
    durationMs: Math.round(durationMs),
    avgFps: durationMs > 0 ? Number(((frames / durationMs) * 1000).toFixed(1)) : 0,
    p50FrameMs: Number(p50.toFixed(2)),
    p95FrameMs: Number(p95.toFixed(2)),
    maxFrameMs: Number(max.toFixed(2)),
    worstFps: p95 > 0 ? Number((1000 / p95).toFixed(1)) : 0,
    droppedFrames: dropped,
    longFrames: long,
    droppedRatio: Number((dropped / frames).toFixed(4)),
  };
}

/**
 * 常驻采样的帧率监视器（PerfHud 使用）。
 * 每 500ms 通过 listener 上报一次滚动窗口内的统计。
 */
export class FrameSampler {
  private readonly listener: ((report: FrameReport) => void) | null;
  private deltas: number[] = [];
  private lastTimestamp = 0;
  private rafId: number | null = null;
  private windowStart = 0;

  constructor(listener: ((report: FrameReport) => void) | null = null) {
    this.listener = listener;
  }

  isRunning(): boolean {
    return this.rafId !== null;
  }

  start(): void {
    if (this.rafId !== null) return;
    this.deltas = [];
    this.lastTimestamp = 0;
    this.windowStart = 0;

    const tick = (timestamp: number): void => {
      if (this.lastTimestamp > 0) {
        this.deltas.push(timestamp - this.lastTimestamp);
      }
      this.lastTimestamp = timestamp;
      if (this.windowStart === 0) this.windowStart = timestamp;

      if (this.listener !== null && timestamp - this.windowStart >= 500) {
        const windowDeltas = this.deltas.slice(-60);
        this.listener(computeFrameReport(windowDeltas));
        this.windowStart = timestamp;
      }
      this.rafId = window.requestAnimationFrame(tick);
    };

    this.rafId = window.requestAnimationFrame(tick);
  }

  stop(): FrameReport {
    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const report = computeFrameReport(this.deltas);
    this.deltas = [];
    return report;
  }
}
